import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Panel } from "../../components/Panel/Panel.jsx";
import { DashboardPage, createDashboardLayout } from "../../components/DashboardPage/DashboardPage.jsx";
import { createSectionNavigationBindings } from "../../lib/shortcuts/createSectionNavigationBindings.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";
import "../../components/ShortcutReference/ShortcutReference.css";
import "../../components/SideMenu/SideMenu.css";
import "./HelpTipsPane.css";

const helpTipsLayout = createDashboardLayout({
  columns: [
    "minmax(0, 1.7fr)",
    "minmax(16rem, 0.85fr)"
  ],
  rows: [
    { size: "fill" }
  ],
  areas: [
    ["reference", "categories"]
  ]
});

export function HelpTipsPane({ getTranslationNode = () => null, t = (key) => key }) {
  const groupRefs = useRef(new Map());
  const referenceGroupsRef = useRef(null);
  const tipsNode = getTranslationNode("help.tips");
  const tipGroups = useMemo(
    () => Object.entries(tipsNode?.groups ?? {}).map(([key, group]) => ({
      key,
      label: group?.label ?? key,
      tips: Object.entries(group?.items ?? {}).map(([itemKey, item]) => ({
        id: itemKey,
        label: item?.label ?? itemKey,
        note: item?.note ?? ""
      }))
    })),
    [tipsNode]
  );
  const [activeGroupKey, setActiveGroupKey] = useState(tipGroups[0]?.key ?? "");

  useEffect(() => {
    if (!tipGroups.some((group) => group.key === activeGroupKey)) {
      setActiveGroupKey(tipGroups[0]?.key ?? "");
    }
  }, [activeGroupKey, tipGroups]);

  useEffect(() => {
    referenceGroupsRef.current?.focus({ preventScroll: true });
  }, []);

  function scrollToGroup(groupKey) {
    setActiveGroupKey(groupKey);
    groupRefs.current.get(groupKey)?.scrollIntoView({
      block: "start",
      behavior: "smooth"
    });
    referenceGroupsRef.current?.focus({ preventScroll: true });
  }

  const sectionOptions = useMemo(
    () => tipGroups.map((group) => ({ value: group.key, label: group.label })),
    [tipGroups]
  );
  const pageSectionBindings = useMemo(() => createSectionNavigationBindings({
    options: sectionOptions,
    value: activeGroupKey,
    onChange: (nextValue) => {
      scrollToGroup(nextValue);
    }
  }), [activeGroupKey, sectionOptions]);

  useShortcutBindings(window, pageSectionBindings);

  return (
    <DashboardPage
      layout={helpTipsLayout}
      slots={[
        {
          key: "reference",
          area: "reference",
          content: (
            <Panel
              className="help-tips-reference"
              header={t("help.tips.title")}
            >
              <div class="shortcut-reference shortcut-reference--embedded">
                <div class="shortcut-reference-groups help-tips-groups" ref={referenceGroupsRef} tabIndex={-1}>
                  {tipGroups.map((group) => (
                    <section
                      class="shortcut-group"
                      key={group.key}
                      ref={(element) => {
                        if (element) {
                          groupRefs.current.set(group.key, element);
                        } else {
                          groupRefs.current.delete(group.key);
                        }
                      }}
                    >
                      <h3 class="shortcut-group-title">{group.label}</h3>

                      <div class="shortcut-list">
                        {group.tips.map((tip) => (
                          <div class="shortcut-row help-tip-row" key={tip.id}>
                            <div class="shortcut-copy">
                              <p class="shortcut-label">{tip.label}</p>
                              <p class="shortcut-note">{tip.note}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </div>
            </Panel>
          )
        },
        {
          key: "categories",
          area: "categories",
          fill: false,
          content: (
            <Panel
              className="help-tips-categories"
              header={t("help.tips.categories")}
            >
              <nav class="side-menu help-tip-category-nav">
                {tipGroups.map((group) => (
                  <button
                    class={`side-menu-item help-tip-category-button${group.key === activeGroupKey ? " active" : ""}`}
                    key={group.key}
                    type="button"
                    onClick={() => scrollToGroup(group.key)}
                  >
                    {group.label}
                  </button>
                ))}
              </nav>
            </Panel>
          )
        }
      ]}
    />
  );
}
